import React, { useState, createContext, useEffect, useContext } from "react";
import { useNavigate } from 'react-router-dom';

import axios from 'api/axios';
import { ContextBlock } from "./ContextBlock";

import { randomId } from '@mui/x-data-grid-generator';

const ContextTraining = createContext();

const TrainingProvider = ({ children }) => {
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(window.location.search);

  const TRAINING_URL = 'training';
  const TRAINING_STAFFING_URL = 'staffing';
  const TRAINING_STATUS_URL = 'status';
  const TRAINING_STATUS_VALIDE = '300';
  const TRAINING_STATUS_IN_PROGRESS = '200';


  //params context
  const { 
    setOpenSnackBar, 
    setSnackBarType, 
    setSnackBarMessage 
  } = useContext(ContextBlock);

  var elementIdParam = parseInt(queryParams.get('id'));
  if (isNaN(elementIdParam)) elementIdParam = 0;
  const [trainingId, setTrainingId] = useState(elementIdParam);
  const [training, setTraining] = useState({});
  const [trainingRows, setTrainingRows] = useState([]);
  const [trainingSessionStaffingRows, setTrainingSessionStaffingRows] = useState([]);
  const [trainingLoaded, setTrainingLoaded] = useState(false);
  const [trainingLoading, setTrainingLoading] = useState(false);
  

  const rowsFromApiFormat = (data) => {
    const dataFormat = {};
    if (!!data !== false) {
      dataFormat.id = trainingId;
      dataFormat.name = data.name;
      dataFormat.code = data.code;
      dataFormat.inter = data.inter;
      dataFormat.manager = data.manager;
      dataFormat.subsessions = data.subsessions;
      dataFormat.status = data?.status ? data.status : {};
      //If training status is Validated => training is no longer writable
      dataFormat.isWritable = data?.status?.code == TRAINING_STATUS_VALIDE ? false : true;
      dataFormat.placeInfo = data.placeInfo ? data.placeInfo?.cityCode + " " + data.placeInfo?.city : '';
      dataFormat.endDate = data.endDate ? new Date(Date.parse(data.endDate)) : null;
      dataFormat.startDate = data.startDate ? new Date(Date.parse(data.startDate)) : null;
      dataFormat.customer = data.customers?.length > 0 ? data.customers[0]?.entity?.name : '';

      dataFormat.qualityExpectations = data.qualityExpectations;
      dataFormat.qualitySuccessConditions = data.qualitySuccessConditions;

      if (dataFormat.endDate) {
        dataFormat.humanReadableEndDate = dataFormat.endDate.getDate() + ' ';
        dataFormat.humanReadableEndDate += dataFormat.endDate.toLocaleString('default', { month: 'long' }) + ' ';
        dataFormat.humanReadableEndDate += dataFormat.endDate.getFullYear();
      }
      if (dataFormat.startDate) {
        dataFormat.humanReadableStartDate = dataFormat.startDate.getDate() + ' ';
        dataFormat.humanReadableStartDate += dataFormat.startDate.toLocaleString('default', { month: 'long' }) + ' ';
        dataFormat.humanReadableStartDate += dataFormat.startDate.getFullYear();
      }
    }

    return dataFormat;
  }

  const getTraining = async () => {
    try {
      setTrainingLoading(true);

      //config call API
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        }
      }
      const response = await axios.get(`${TRAINING_URL}/${trainingId}`, config);

      const rows = [];
      if (response.data?.data) {
        var row = rowsFromApiFormat(response.data?.data);
        setTraining(row);
        rows.push(row);
        setTrainingRows(rows);
        prepareTrainingSessionStaffingRows(response.data?.data);
      }
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate('/deconnexion');
      }
      else {
        setSnackBarMessage('Erreur technique! Veuillez réessayer ou contacter notre support.');
        setSnackBarType('error');
        setOpenSnackBar(true);
      }
    } finally {
      setTrainingLoading(false);
      setTrainingLoaded(true);
    }
  }
  
  const prepareTrainingSessionStaffingRows = (data) => {
    //set TrainingSessionStaffingRows only if training is validated
    if(data.status?.code == TRAINING_STATUS_VALIDE){
      var sessions = [];
      data.subsessions.map((session) => {
        sessions.push({
          sessionId : session.id,
          digiformaSessionId : session.id_training_session,
          n_days : session.n_days,
          thematic : session.program_name || session.name,
          startDate : session.start_date,
          endDate : session.end_date,
          rows : session.staffings
        });
      });
      setTrainingSessionStaffingRows(sessions);
    }
  }

  const saveSessionStaffing = async (rows, sessionId, digiformaSessionId) => {
    //get existing staffingrows in DB for the given session
    var existingStaffingRows = [];
    training.subsessions.filter((session) => {
      // Match the exact date-row (session.id), not the shared Digiforma module id — otherwise
      // staffing rows belonging to another date of the same module get treated as "unchecked"
      // and deleted.
      if(session.id == sessionId){
        session.staffings.map((staffing) => {
          existingStaffingRows.push({
            id : staffing.rowId,
            sessionId : staffing.sessionId,
            instructorId : staffing.id
          });
        });
      }
    });
    //foreach received row, figure out which are new (to add) — purely synchronous matching first
    const rowsToAdd = [];
    rows.forEach((checkedRow) => {
      var match = false;
      existingStaffingRows = existingStaffingRows.filter((existingRow) => {
        if(sessionId == existingRow.sessionId && checkedRow.id == existingRow.instructorId){
          //If there is a match with an existing, no return to remove if from the existing list (we'll delete all remaining at the end of the loop)
          match = true;
        }else{
          return existingRow;
        }
      });
      //If there is no existing match => it has to be added
      if(!match){
        checkedRow.digiformaSessionId = digiformaSessionId;
        checkedRow.sessionId = sessionId;
        rowsToAdd.push(checkedRow);
      }
    });
    //Actually wait for the API calls: add the new rows, then delete the remaining
    //(unchecked) existing rows — awaited so callers can rely on this being fully done.
    await Promise.all(rowsToAdd.map((checkedRow) => postStaffingRow(checkedRow)));
    await Promise.all(existingStaffingRows.map((rowToDelete) => deleteStaffingRow(rowToDelete.id)));

    setSnackBarMessage('Les informations ont été enregistrées avec succès.');
    setSnackBarType('success');
    setOpenSnackBar(true);
  }

  const postStaffingRow = async (checkedRow) => {
    try {
        const data = {
          id_training: trainingId,
          id_instructor: parseInt(checkedRow.id),
          id_training_session: parseInt(checkedRow.sessionId),
          id_training_session_digiforma: parseInt(checkedRow.digiformaSessionId), 
          start_date: checkedRow.startDate,
          end_date: checkedRow.endDate
        };
        const config = {
          method: 'post',
          url: `${TRAINING_URL}/${trainingId}/${TRAINING_STAFFING_URL}`,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json'
          },
          data: data
        };
        await axios(config);

        return {success : true};
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate('/deconnexion');
      } else {
        setSnackBarMessage('Erreur technique! Veuillez réessayer ou contacter notre support.');
        setSnackBarType('error');
        setOpenSnackBar(true);
        return {success : false};
      }
    }
  }

  const deleteStaffingRow = async (staffingId) => {
    try {
      //config call API
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        }
      }
      await axios.delete(`${TRAINING_URL}/${trainingId}/${TRAINING_STAFFING_URL}/${staffingId}`, config);
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate('/deconnexion');
      }
      else {
        setSnackBarMessage('Erreur technique! Veuillez réessayer ou contacter notre support.');
        setSnackBarType('error');
        setOpenSnackBar(true);
      }
    }
  }

  const validateTrainingStaffing = async () => {
    var updateTraining = await updateTrainingStatus(TRAINING_STATUS_VALIDE);
    //If update ok => refresh page
    if(updateTraining.success === true){
      window.location.reload(); 
    }
  }  

  const cancelTrainingStaffing = async () => {
    var updateTraining = await updateTrainingStatus(TRAINING_STATUS_IN_PROGRESS);
    //If update ok => refresh page
    if(updateTraining.success === true){
      window.location.reload(); 
    }
  }  

  const updateTrainingStatus = async (statusCode) => {
    try {
      const config = {
        method: 'post',
        url: `${TRAINING_URL}/${trainingId}/${TRAINING_STATUS_URL}`,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        data: { code: statusCode }
      };
      await axios(config);
      return {success : true};
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate('/deconnexion');
        return {success : false};
      } else if (err.response?.status === 409) {
        setSnackBarMessage(err.response?.data?.message || 'Cette action n\'est pas autorisée.');
        setSnackBarType('error');
        setOpenSnackBar(true);
        return {success : false};
      } else {
        setSnackBarMessage('Erreur technique! Veuillez réessayer ou contacter notre support.');
        setSnackBarType('error');
        setOpenSnackBar(true);
        return {success : false};
      }
    }
  }


  return (
    <ContextTraining.Provider
      value=
      {{
        TRAINING_STATUS_VALIDE,
        trainingId, setTrainingId,
        training, setTraining,
        trainingSessionStaffingRows, setTrainingSessionStaffingRows,
        trainingRows, setTrainingRows,
        trainingLoaded,
        trainingLoading,
        getTraining,
        saveSessionStaffing,
        updateTrainingStatus,
        validateTrainingStaffing,
        cancelTrainingStaffing
      }}
    >
      {children}
    </ContextTraining.Provider>
  );
};

export { TrainingProvider, ContextTraining };
